import { useState, useEffect } from 'react';
import api from '../../../../api';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { FiTrendingUp } from 'react-icons/fi';
import { BiCalendarCheck } from 'react-icons/bi';
import { HiOutlineChartBar } from 'react-icons/hi';
import PKTScoresSummary from '../../../../Data/DashboardData/QualityAuditor/PKTScoresSummary.json';
import QAPerformanceReportsFilter from '../../BiReports/PerformanceTab/QAPerformanceReportsFilter';
import PktScores from './PktScores'

// Custom Components
import PerformanceCard from './PerformanceCard';
import AttendanceOverview from './AttendanceOverview';
import ToolAndPKTSection from './ToolAndPKTSection';
import ErrorCards from './ErrorCards';

function QualityManagerDashboardMain({ ManagerId }) {
  const { accessToken } = useAuth();

  const [pktPercentage, setPktPercentage] = useState("0");
  const [effectivenessPercentage, setEffectivenessPercentage] = useState("0%");
  const [nonFatalErrors, setNonFatalErrors] = useState([]);
  const [fatalErrors, setFatalErrors] = useState([]);
  const [auditedTarget, setAuditedTarget] = useState(0);
  const [auditedCompleted, setAuditedCompleted] = useState(0);
  const [yesterdayPerformanceData, setYesterdayPerformanceData] = useState();
  const [monthlyPerformanceData, setMonthlyPerformanceData] = useState();
  const [yearlyPerformanceData, setYearlyPerformanceData] = useState();

  const [attendanceOverview, setAttendanceOverview] = useState({
    dayShiftworkingDays: 0,
    nightShiftworkingDays: 0,
    leaveDays: 0,
    attendancePercentage: '0%',
  });

  const [filters, setFilters] = useState({
    clientId: '',
    scopeId: '',
    user: '',
    startDate: '',
    endDate: '',
  });

  const handleFilterChange = (updatedFilters) => {
    const mappedFilters = {
      clientId: updatedFilters.client || '',
      scopeId: updatedFilters.scope || '',
      user: updatedFilters.user || '',
      startDate: updatedFilters.startDate || '',
      endDate: updatedFilters.endDate || '',
      status: updatedFilters.status || '',
    };
    setFilters(mappedFilters);
  };

  const buildParams = (filters) => {
    const params = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        params[key] = filters[key];
      }
    });
    return params;
  };

  const fetchAttendanceOverview = async () => {
    try {
      const baseUrl = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrl}/qualityManagerDashBoard/getQaManagerAttendanceOverView`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: buildParams(filters),
      });

      if (response.data) {
        setAttendanceOverview({
          dayShiftworkingDays: response.data.dayShiftworkingDays,
          nightShiftworkingDays: response.data.nightShiftworkingDays,
          leaveDays: response.data.leaveDays,
          attendancePercentage: response.data.attendancePercentage,
        });
      }
    } catch (error) {
      console.error("Error fetching QA Manager Attendance Overview:", error);
    }
  };

  const fetchAuditedCount = async () => {
    try {
      const baseUrl = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrl}/qualityManagerDashBoard/getAuditedCount`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: buildParams(filters),
      });
      if (response.data.success) {
        setAuditedTarget(response.data.auditedTarget);
        setAuditedCompleted(response.data.auditedCompleted);
      }
    } catch (error) {
      console.error("API error fetching audited count:", error);
    }
  };

  const fetchPKT = async () => {
    try {
      const baseUrl = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrl}/qualityManagerDashBoard/getPKTPercentage`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: buildParams(filters),
      });
      if (response.data.success) {
        setPktPercentage(`${Math.round(response.data.data.pkt_percentage)}%`);
      }
    } catch (error) {
      console.error("Error fetching PKT:", error);
    }
  };

  const fetchToolEffectiveness = async () => {
    try {
      const baseUrl = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrl}/qualityManagerDashBoard/getQaManagerToolEffectiveness`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: buildParams(filters),
      });
      if (response.data && response.data.toolEffectivenessPercentage) {
        setEffectivenessPercentage(response.data.toolEffectivenessPercentage);
      }
    } catch (error) {
      console.error("Error fetching tool effectiveness data:", error);
    }
  };

  const fetchErrorSeverity = async () => {
    try {
      const baseUrl = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrl}/qualityManagerDashBoard/getErrorSeviority`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: buildParams(filters),
      });
      if (response.data.success) {
        setFatalErrors(response.data.topFatalErrors);
        setNonFatalErrors(response.data.topNonFatalErrors);
      }
    } catch (error) {
      console.error("Error fetching error severity data:", error);
    }
  };

  const fetchPerformanceData = async () => {
    try {
      const baseUrl = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrl}/qualityManagerDashBoard/getDashBoardPreviousDayAuditedPercentage`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: buildParams(filters),
      });
      if (response.data.success) {
        setYesterdayPerformanceData(`${Math.round(response.data.data.YesterdayPercentage)}`);
      }
    } catch (error) {
      console.error("Error fetching previous day performance data:", error);
    }
  };

  const fetchMonthlyPerformanceData = async () => {
    try {
      const baseUrl = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrl}/qualityManagerDashBoard/getDashBoardMonthlyAuditedPercentage`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: buildParams(filters),
      });
      if (response.data.success) {
        setMonthlyPerformanceData(`${Math.round(response.data.data.MonthlyPercentage)}`);
      }
    } catch (error) {
      console.error("Error fetching monthly performance data:", error);
    }
  };

  const fetchYearlyPerformanceData = async () => {
    try {
      const baseUrl = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrl}/qualityManagerDashBoard/getDashBoardYearlyAuditedPercentage`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: buildParams(filters),
      });
      if (response.data.success) {
        setYearlyPerformanceData(`${Math.round(response.data.data.YearlyPercentage)}`);
      }
    } catch (error) {
      console.error("Error fetching yearly performance data:", error);
    }
  };

  useEffect(() => {
    if (!accessToken) return;

    fetchAttendanceOverview();
    fetchAuditedCount();
    fetchPKT();
    fetchToolEffectiveness();
    fetchErrorSeverity();
    fetchPerformanceData();
    fetchMonthlyPerformanceData();
    fetchYearlyPerformanceData();
  }, [ManagerId, accessToken, filters]);

  return (
    <div className="bg-gray-100 min-h-screen py-8 px-4">
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .animate-marquee {
          display: inline-block;
          white-space: nowrap;
          animation: marquee 15s linear infinite;
        }
      `}</style>

      <h1 className="text-3xl text-center font-bold mb-6 text-black">
        Quality Manager Dashboard
      </h1> 

      <div>
        <QAPerformanceReportsFilter isChecked={false} onFilterChange={handleFilterChange} />
      </div>

      <div className="space-y-24">
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-0">
            <PerformanceCard icon={<FiTrendingUp />} title="Yesterday's Performance" value={yesterdayPerformanceData} />
            <PerformanceCard icon={<BiCalendarCheck />} title="MTD Performance" value={monthlyPerformanceData} />
            <PerformanceCard icon={<HiOutlineChartBar />} title="YTD Performance" value={yearlyPerformanceData} />
          </div>
        </div>

        <div>
          <h2 className="text-3xl font-semibold text-[#020306] mb-4 flex items-center justify-center gap-2">
            🗓️ Attendance Overview
          </h2>
          <AttendanceOverview
            dayShiftworkingDays={attendanceOverview.dayShiftworkingDays}
            nightShiftworkingDays={attendanceOverview.nightShiftworkingDays}
            leaveDays={attendanceOverview.leaveDays}
            attendancePercentage={attendanceOverview.attendancePercentage}
          />
        </div>

        <div>
          <h2 className="text-3xl font-semibold text-[#020306] mb-4 text-center">
            📊 Tool & PKT Scores
          </h2>
          <ToolAndPKTSection
            effectivenessPercentage={effectivenessPercentage}
            pktPercentage={pktPercentage}
            pktLabel={PKTScoresSummary.Description}
            auditTarget={auditedTarget}
            auditCompleted={auditedCompleted}
          />
        </div>
        <div>
          <h2 className="text-3xl font-semibold text-[#020306] mb-4 text-center">
            ⚠️ Top Errors
          </h2>
          <ErrorCards fatalErrors={fatalErrors} nonFatalErrors={nonFatalErrors} />
        </div>
        <div>
           <h2 className="text-4xl font-semibold text-[#020306] mb-4 text-center">
            📊PKTScoresSummary
           </h2>
          <PktScores/>
        </div>
      </div>
    </div>
  );
}

export default QualityManagerDashboardMain;
